<!-- @format -->

<template>
	<pc-dialog
		v-model="visabled"
		destroy-on-close
		title="CMDB同步"
		width="70%"
		:confirm-button-text="confirmButtonText"
		:cancel-button-text="cancelButtonText"
		:show-confirm-button="showFlag && showTable"
		@on-cancel="onCancel"
		@on-confirm="onConfirm"
		@close="onClose"
	>
		<template #header>
			<div v-if="showFlag">
				模型:
				<el-select
					v-if="bussinessList.length > 1"
					v-model="bussinessCode"
					placeholder="请选择模型"
					@change="handleChange"
				>
					<el-option v-for="item in bussinessList" :key="item.value" :label="item.label" :value="item.value" />
				</el-select>
				<el-tag v-else>
					{{ bussinessList.find((item) => item.value === bussinessCode)?.label || '' }}
				</el-tag>
			</div>
			<div v-else>资源库中无相应的信息</div>
		</template>
		<pc-table-warp
			v-if="showFlag && showTable"
			ref="tableRef"
			v-model:page-value="pageValue"
			v-model:search-value="searchFormDatas"
			:search-list="searchList"
			row-key="objectid"
			:data="tableData.list"
			:total="tableData.total"
			:show-total="true"
			:can-more-search="allFields.length > 3 ? true : false"
			:can-add-more-search="allFields.length > 3 ? true : false"
			:can-add-more-column="true"
			:more-search-list="moreSearchList"
			:default-check-columns-list="defaultCheckColumnsList"
			:column-list="columnList"
			:search-span="8"
			:multiple="true"
			:loading="loading"
			max-height="350px"
			@on-search="onSearch"
			@on-search-reset="onSearch"
			@page-change="onPageChange"
		>
			<template v-for="item in columnList" :key="item.code" #[`column-default-${item.code}`]="scope">
				{{ scope.row.fieldMap[scope.column.property] }}
			</template>
		</pc-table-warp>
		<el-empty v-if="!showTable" description="资源库中无相应的信息" />
	</pc-dialog>
</template>

<script lang="ts" setup>
	import { PcDialog, PcTableWarp } from '@/components';
	import { getDataByCode, getFieldsByCode, getInfoByCode } from '@/api/bussiness';
	import { deepClone } from '@/utils';

	interface Props {
		modelValue: boolean;
		confirmButtonText?: string; // 确定按钮文案
		cancelButtonText?: string; // 取消按钮文案
		code: string;
		conditions?: string;
	}

	const props = withDefaults(defineProps<Props>(), {
		confirmButtonText: '保存',
		cancelButtonText: '取消',
		conditions: '',
		code: '',
	});
	const emit = defineEmits(['update:modelValue', 'onConfirm', 'onCancel', 'close']);
	const tableRef = ref<InstanceType<typeof PcTableWarp>>();
	const showFlag = ref(true);
	const showTable = ref(true);
	const loading = ref(false);
	const searchFormDatas = ref<any>({});
	const searchList = ref<any>([]);
	const defaultCheckColumnsList: any = ref([]);
	const columnList: any = ref([]);
	const moreSearchList: any = ref([]);
	const tableData = ref<any>({
		list: [],
		total: 0,
	});
	const pageValue = ref({ pageNum: 1, pageSize: 10 });
	const bussinessList = ref<any>([]);
	const bussinessCode = ref<string>('');
	const allFields = ref<any>([]);

	const visabled = computed({
		get() {
			return props.modelValue || false;
		},
		set(val) {
			emit('update:modelValue', val);
		},
	});

	const onConfirm = (callback) => {
		const selectList = tableRef.value?.tableRef?.getSelectionRows();
		//深度拷贝list
		const list = deepClone(selectList || []);
		const data = list.map((item) => {
			const obj = { ...item };
			allFields.value.forEach((itm) => {
				if (itm.autoCode) {
					if (itm.autoCode !== itm.code) {
						obj['fieldMap'][itm.autoCode] = item.fieldMap[itm.code];
						delete obj['fieldMap'][itm.code];
					}
				}
			});
			return obj;
		});
		emit('onConfirm', callback, data);
	};

	const onCancel = (callback) => {
		emit('onCancel', callback);
	};

	const onClose = () => {
		emit('close');
	};

	const handleChange = () => {
		tableRef.value?.tableRef?.clearSelection();
		pageValue.value.pageNum = 1;
		getFieldsList();
	};

	const onSearch = () => {
		pageValue.value.pageNum = 1;
		getDataList();
	};
	const onPageChange = (val) => {
		pageValue.value = val;
		getDataList();
	};

	const getDataList = async () => {
		loading.value = true;
		const data = {
			code: props.code,
			resCode: bussinessCode.value,
			conditions: { ...searchFormDatas.value },
			defaultConditions: props.conditions,
			...pageValue.value,
		};
		const res = await getDataByCode(data);
		tableData.value = {
			list: res.data,
			total: res.total,
		};
		loading.value = false;
	};

	const getFieldsList = async () => {
		allFields.value = [];
		const data = {
			code: props.code,
			resCode: bussinessCode.value,
			conditions: { ...searchFormDatas.value },
			defaultConditions: props.conditions,
			...pageValue.value,
		};
		const res = await getFieldsByCode(data);
		if (!res) {
			showTable.value = false;
			return false;
		}
		for (const key in res) {
			allFields.value.push({
				label: res[key].label,
				code: key,
				autoCode: res[key].autoCode,
				type: 'input',
				pcProps: {
					placeholder: `请输入${res[key].label}`,
				},
			});
		}
		//取前三个
		searchList.value = allFields.value.slice(0, 3);
		//取剩下的
		moreSearchList.value = allFields.value.slice(3);
		columnList.value = allFields.value;
		//默认选中前10个字段
		defaultCheckColumnsList.value = allFields.value.slice(0, 10).map((item) => item.code);
		getDataList();
	};
	const getBussinessList = async () => {
		const res = await getInfoByCode({ code: props.code });
		if (!(res?.resList?.length > 0)) {
			showFlag.value = false;
			return false;
		}
		bussinessList.value =
			res?.resList?.map((item) => {
				return {
					label: item.resName,
					value: item.restypeCode,
				};
			}) || [];
		bussinessCode.value = bussinessList.value[0].value || '';
		getFieldsList();
	};
	//监听visible变化，重新获取数据
	watch(
		() => visabled.value,
		async (val) => {
			if (val) {
				showTable.value = true;
				showFlag.value = true;
				bussinessList.value = [];
				bussinessCode.value = '';
				searchList.value = [];
				moreSearchList.value = [];
				columnList.value = [];
				defaultCheckColumnsList.value = [];
				tableData.value = { list: [], total: 0 };
				pageValue.value = { pageNum: 1, pageSize: 10 };
				allFields.value = [];
				getBussinessList();
			}
		},
	);
</script>

<style lang="scss" scoped></style>
